import { supabase } from '../lib/supabase'
import QRCode from 'qrcode'
import { UserLoggingService } from './userLoggingService'

export interface CreateQRCodeData {
  user_id: string
  campaign_id?: string | null
  internal_ad_id?: string | null
  node_id?: string | null
  name: string
  qr_type: 'Static QR' | 'Dynamic QR'
  destination_url: string
  qr_code_url?: string  // Made optional since we'll generate it
  cta?: string
  design_settings?: Record<string, any>
  /**
   * Optional caller-supplied token to persist as this QR's `code` — the value
   * encoded into the QR image as `/qr/{code}`.
   *
   * The Campaign Builder mints a token at step 5 so it can preview a QR before
   * the campaign is paid for. Passing it here makes the previewed link and the
   * QR created at payment the SAME link. Uniqueness is still enforced:
   * `idx_qr_codes_code_lower` is unique, so a token already in use gets a
   * random suffix appended by the retry loop below.
   */
  code?: string
  status?: 'Active' | 'Inactive' | 'Expired'
  expires_at?: string | null
  goal_type?: string | null
  media_type?: string | null
  location_data?: Record<string, any>
  performance_data?: Record<string, any>
}

export interface QRCodeServiceResponse {
  success: boolean
  data?: any
  error?: string
}

export const qrCodeService = {
  // Create a new QR code and save it to storage
  async createQRCode(qrCodeData: CreateQRCodeData): Promise<QRCodeServiceResponse> {
    try {
      // Generate a URL-safe, human-friendly `code` token for the QR so
      // short-token URLs (like `QR-MH2TO2SM-...`) can be resolved against the DB.
      // We must generate the `code` BEFORE rendering the QR image so the
      // image encodes the canonical redirect URL (`/qr/{code}`) while
      // `destination_url` remains the raw user-entered URL.
      const makeUrlSafe = (s: string) => {
        if (!s) return '';
        return s
          .toString()
          .replace(/[^A-Za-z0-9-_]/g, '-')
          .replace(/-+/g, '-')
          .replace(/^[-_]+|[-_]+$/g, '')
          .slice(0, 48);
      };

      const generateRandomSuffix = () => Math.random().toString(36).substring(2, 8).toUpperCase();

      const isDuplicateCodeError = (err: unknown) => {
        const message = err instanceof Error
          ? err.message
          : err && typeof err === 'object' && 'message' in err
            ? String((err as { message?: unknown }).message || '')
            : String(err || '');
        return message.includes('duplicate key value violates unique constraint') || message.includes('idx_qr_codes_code_lower');
      };

      const tryGenerateUniqueCode = async (baseCandidate: string) => {
        let candidate = makeUrlSafe(baseCandidate) || `qr-${Date.now().toString(36)}`;
        for (let i = 0; i < 6; i++) {
          try {
            const { data: existing, error: checkErr } = await supabase
              .from('qr_codes')
              .select('id')
              .ilike('code', candidate)
              .limit(1);

            if (checkErr) {
              candidate = `${candidate}-${generateRandomSuffix()}`;
              continue;
            }

            if (!existing || existing.length === 0) {
              return candidate;
            }

            candidate = `${candidate}-${generateRandomSuffix()}`;
          } catch (e) {
            candidate = `${candidate}-${generateRandomSuffix()}`;
          }
        }

        return `${candidate}-${Date.now()}`;
      };

      // Determine base for code generation.
      // A caller-supplied `code` wins: the Campaign Builder previews a QR at
      // step 5 using a token it minted client-side, and that same token must be
      // the one persisted here or the previewed link never resolves.
      // Falls back to the historical name/destination slug.
      let resolvedCode = '';
      try {
        const base = qrCodeData.code || qrCodeData.name || qrCodeData.destination_url || qrCodeData.campaign_id || `qr-${Date.now()}`;
        resolvedCode = await tryGenerateUniqueCode(base);
      } catch (genErr) {
        console.warn('Failed to generate unique QR code token (falling back):', genErr);
        resolvedCode = `qr-${Date.now()}`;
      }

      const origin = typeof window !== 'undefined' && window.location ? window.location.origin : '';
      let lastError = '';

      for (let attempt = 0; attempt < 4; attempt++) {
        // Build the canonical redirect URL encoded into the QR image.
        const redirectUrl = origin ? `${origin}/qr/${resolvedCode}` : `/qr/${resolvedCode}`;

        // Generate QR code as data URL that encodes the redirect URL (not the raw destination)
        let qrCodeUrl = '';
        try {
          qrCodeUrl = await QRCode.toDataURL(redirectUrl, {
            width: 200,
            margin: 1,
            color: {
              dark: qrCodeData.design_settings?.primary_color || '#000000',
              light: qrCodeData.design_settings?.secondary_color || '#FFFFFF'
            }
          });
        } catch (error) {
          console.error('❌ Error generating QR code:', error);
          return { success: false, error: 'Failed to generate QR code' };
        }

        // Upload QR code image to storage bucket in the qr-codes folder
        let storedQrUrl = '';
        try {
          const response = await fetch(qrCodeUrl);
          const blob = await response.blob();
          const fileName = `qr-${qrCodeData.campaign_id || 'temp'}-${Date.now()}.png`;
          const filePath = `qr-codes/${qrCodeData.user_id}/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from('campaign-assets')
            .upload(filePath, blob, {
              cacheControl: '3600',
              upsert: true
            });

          if (uploadError) {
            console.error('❌ Error uploading QR code to storage:', uploadError);
            storedQrUrl = qrCodeUrl;
          } else {
            const { data: publicData } = supabase.storage
              .from('campaign-assets')
              .getPublicUrl(filePath);

            if (publicData?.publicUrl) {
              storedQrUrl = publicData.publicUrl;
            } else {
              console.warn('⚠️ Could not get public URL, falling back to data URL');
              storedQrUrl = qrCodeUrl;
            }
          }
        } catch (storageError) {
          console.error('❌ Error in QR code storage process:', storageError);
          storedQrUrl = qrCodeUrl;
        }

        const insertData = {
          ...qrCodeData,
          qr_code_url: storedQrUrl,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          design_settings: qrCodeData.design_settings || {},
          location_data: qrCodeData.location_data || {},
          performance_data: qrCodeData.performance_data || {},
          cta: qrCodeData.cta || '',
          goal_type: qrCodeData.goal_type || null,
          media_type: qrCodeData.media_type || null
        };
        (insertData as any).code = resolvedCode;

        const { data, error } = await supabase
          .from('qr_codes')
          .insert([insertData])
          .select()
          .single();

        if (!error && data) {
          Promise.resolve(UserLoggingService.logQRCodeGenerated(
            data.id,
            data.destination_url || 'Unknown destination'
          )).catch(err => console.error('Failed to log QR code generation:', err));

          return { success: true, data };
        }

        lastError = error?.message || 'Failed to create QR code';

        if (!isDuplicateCodeError(error) || attempt === 3) {
          return { success: false, error: lastError };
        }

        resolvedCode = `${resolvedCode}-${generateRandomSuffix()}`;
      }

      return { success: false, error: lastError || 'Failed to create QR code' };
    } catch (error) {
      console.error('💥 Exception in createQRCode:', error);
      return { success: false, error: 'Failed to create QR code' }
    }
  },

  /**
   * Attach/refresh the QR row that already carries this token, keyed by `code`.
   *
   * Used by the Campaign Builder: step 5 creates the row so the previewed link
   * resolves before payment, and step 7 adopts that same row (adding campaign_id
   * / node_id) instead of inserting a duplicate with a suffixed code.
   *
   * Updates IN PLACE, never delete+insert: `qr_scans_qr_code_id_fkey` is
   * ON DELETE CASCADE, so deleting a QR row would destroy its scan history.
   *
   * Refuses to adopt a row that is already bound to a DIFFERENT node, which is
   * what keeps multi-node campaigns creating one distinct QR per node.
   */
  async attachExistingQRCode(
    userId: string,
    code: string,
    updates: {
      campaign_id?: string | null;
      node_id?: string | null;
      destination_url?: string;
      cta?: string | null;
      cta_icon?: string | null;
      name?: string;
      design_settings?: Record<string, any>;
    }
  ): Promise<QRCodeServiceResponse> {
    try {
      const { data: existing, error: findErr } = await supabase
        .from('qr_codes')
        .select('id, node_id, user_id')
        .ilike('code', code)
        .limit(1);

      if (findErr) {
        return { success: false, error: findErr.message };
      }

      if (!existing || existing.length === 0) {
        return { success: false, error: 'QR code not found for adoption' };
      }

      const row = existing[0] as any;

      // Only ever adopt a row this user owns — a caller-supplied token must not
      // let one advertiser capture another's QR.
      if (row.user_id && userId && row.user_id !== userId) {
        return { success: false, error: 'QR code belongs to another user' };
      }

      const incomingNode = updates.node_id ?? null;
      const existingNode = row.node_id ?? null;
      if (existingNode !== null && existingNode !== incomingNode) {
        // Already bound to a sibling node; caller must create its own row.
        return { success: false, error: 'QR code already bound to another node' };
      }

      const { data, error } = await supabase
        .from('qr_codes')
        .update({
          campaign_id: updates.campaign_id ?? null,
          node_id: incomingNode,
          destination_url: updates.destination_url,
          cta: updates.cta ?? '',
          cta_icon: updates.cta_icon ?? '',
          name: updates.name,
          design_settings: updates.design_settings || {},
          updated_at: new Date().toISOString()
        } as any)
        .eq('id', row.id)
        .select()
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Exception in attachExistingQRCode:', error);
      return { success: false, error: 'Failed to attach QR code' };
    }
  },

  // Get all QR codes for a user with pagination
  async getUserQRCodes(
    userId: string,
    page: number = 1,
    pageSize: number = 50
  ): Promise<QRCodeServiceResponse & { total?: number; page?: number; pageSize?: number }> {
    try {
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;
      
      const { data, error, count } = await supabase
        .from('qr_codes')
        .select('*', { count: 'exact' })
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) {
        console.error('❌ Error fetching QR codes:', error)
        return { success: false, error: error.message }
      }

      return { success: true, data: data || [], total: count || 0, page, pageSize }
    } catch (error) {
      console.error('❌ Exception fetching QR codes:', error)
      return { success: false, error: 'Failed to fetch QR codes' }
    }
  },

  // Get all QR codes for admin with pagination
  async getAllQRCodes(
    page = 1,
    pageSize = 50,
    dateRange?: { startDate?: string; endDate?: string }
  ): Promise<{
    success: boolean;
    data?: any[];
    totalCount?: number;
    page?: number;
    pageSize?: number;
    totalPages?: number;
    error?: string;
  }> {
    try {
      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      // Build the query with pagination
      let query = supabase
        .from('qr_codes')
        .select(
          `
          *,
          campaigns (
            name,
            status
          ),
          profiles (
            full_name,
            email
          )
        `,
          { count: 'exact' }
        )
        .order('created_at', { ascending: false });

      // Apply date range filter if provided
      if (dateRange?.startDate) {
        query = query.gte('created_at', dateRange.startDate);
      }
      if (dateRange?.endDate) {
        query = query.lte('created_at', dateRange.endDate);
      }

      // Apply pagination range
      const { data, error, count } = await query.range(from, to);

      if (error) {
        console.error('Error fetching all QR codes:', error);
        return { success: false, error: error.message };
      }

      return {
        success: true,
        data: data || [],
        totalCount: count || 0,
        page,
        pageSize,
        totalPages: Math.ceil((count || 0) / pageSize)
      };
    } catch (error) {
      console.error('Error fetching all QR codes:', error);
      return { success: false, error: 'Failed to fetch all QR codes' };
    }
  },

  // Update QR code
  async updateQRCode(qrCodeId: string, updates: Partial<CreateQRCodeData>): Promise<QRCodeServiceResponse> {
    try {
      // If destination_url is being updated, regenerate the QR code image
      if (updates.destination_url) {
        try {
          // Regenerate QR code as data URL
          const newQrCodeUrl = await QRCode.toDataURL(updates.destination_url, {
            width: 300,
            margin: 1,
            color: {
              dark: '#000000',
              light: '#FFFFFF'
            }
          });

          // Upload to storage
          const response = await fetch(newQrCodeUrl);
          const blob = await response.blob();
          const fileName = `qr-${qrCodeId}-${Date.now()}.png`;
          const filePath = `qr-codes/${updates.user_id || 'unknown'}/${fileName}`;
          
          const { error: uploadError } = await supabase.storage
            .from('campaign-assets')
            .upload(filePath, blob, {
              cacheControl: '3600',
              upsert: true
            });

          if (!uploadError) {
            const { data: publicData } = supabase.storage
              .from('campaign-assets')
              .getPublicUrl(filePath);
              
            if (publicData?.publicUrl) {
              // Update the qr_code_url with the new stored URL
              (updates as any).qr_code_url = publicData.publicUrl;
            }
          }
        } catch (error) {
          console.error('Error regenerating QR code image:', error);
        }
      }

      const updateData = {
        ...updates,
        updated_at: new Date().toISOString(),
        // For JSONB columns, pass the object directly
        ...(updates.design_settings && { design_settings: updates.design_settings })
      };

      const { data, error } = await supabase
        .from('qr_codes')
        .update(updateData)
        .eq('id', qrCodeId)
        .select()
        .single()

      if (error) {
        console.error('Error updating QR code:', error)
        return { success: false, error: error.message }
      }

      // Log QR code update
      const changedFields = Object.keys(updates);
      Promise.resolve(UserLoggingService.logQRCodeUpdated(
        qrCodeId,
        changedFields
      )).catch(err => console.error('Failed to log QR code update:', err));

      return { success: true, data }
    } catch (error) {
      console.error('Error updating QR code:', error)
      return { success: false, error: 'Failed to update QR code' }
    }
  },

  /**
   * Re-render a QR image in a new palette, keeping the encoded target correct.
   *
   * Deliberately separate from updateQRCode, which encodes `destination_url`.
   * The image must encode the same `/qr/<code>` redirect the original carried:
   * that token is what the scan endpoint resolves, and it is how a scan is
   * attributed to the node that displayed it. Encoding the raw destination would
   * make scans work while silently losing per-node attribution.
   */
  async regenerateStyledQRCode(
    qrCodeId: string,
    colours: { primaryColor?: string | null; secondaryColor?: string | null }
  ): Promise<QRCodeServiceResponse> {
    // Shape returned by the select below; kept local because the Supabase client
    // is untyped here and the compiler cannot infer these columns.
    type QrRow = { code?: string | null; user_id?: string | null }
    try {
      const { data: row, error: readError } = await supabase
        .from('qr_codes')
        .select('id, code, user_id, destination_url')
        .eq('id', qrCodeId)
        .single()

      if (readError || !row) {
        return { success: false, error: readError?.message || 'QR code not found' }
      }

      const qrRow = row as unknown as QrRow
      const code = qrRow.code
      if (!code) {
        // Refuse rather than fall back to destination_url: a QR without its
        // redirect token would break attribution, which is worse than a stale
        // colour. Callers surface this as "colours saved, QR not re-styled".
        return { success: false, error: 'QR code has no token; refusing to re-encode' }
      }

      const origin = typeof window !== 'undefined' && window.location ? window.location.origin : ''
      const redirectUrl = origin ? `${origin}/qr/${code}` : `/qr/${code}`

      const dataUrl = await QRCode.toDataURL(redirectUrl, {
        width: 300,
        margin: 1,
        color: {
          dark: colours.primaryColor || '#000000',
          light: colours.secondaryColor || '#FFFFFF',
        },
      })

      const blob = await (await fetch(dataUrl)).blob()
      const filePath = `qr-codes/${qrRow.user_id || 'unknown'}/qr-${qrCodeId}-${Date.now()}.png`

      const { error: uploadError } = await supabase.storage
        .from('campaign-assets')
        .upload(filePath, blob, { cacheControl: '3600', upsert: true })

      if (uploadError) return { success: false, error: uploadError.message }

      const { data: publicData } = supabase.storage.from('campaign-assets').getPublicUrl(filePath)
      const qrCodeUrl = publicData?.publicUrl
      if (!qrCodeUrl) return { success: false, error: 'Failed to resolve uploaded QR URL' }

      const { data, error } = await supabase
        .from('qr_codes')
        .update({
          qr_code_url: qrCodeUrl,
          design_settings: {
            primary_color: colours.primaryColor || '#000000',
            secondary_color: colours.secondaryColor || '#FFFFFF',
          },
          updated_at: new Date().toISOString(),
        })
        .eq('id', qrCodeId)

      if (error) return { success: false, error: error.message }
      return { success: true, data }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to regenerate QR code',
      }
    }
  },

  // Delete QR code
  async deleteQRCode(qrCodeId: string): Promise<QRCodeServiceResponse> {
    try {
      // First get the QR code to find the stored file path
      const { data: qrCode, error: fetchError } = await supabase
        .from('qr_codes')
        .select('qr_code_url')
        .eq('id', qrCodeId)
        .single();

      if (!fetchError && qrCode?.qr_code_url) {
        try {
          // Extract the file path from the URL for deletion
          const urlObj = new URL(qrCode.qr_code_url);
          // Extract path from URL like: https://.../campaign-assets/qr-codes/user-id/filename.png
          const pathParts = urlObj.pathname.split('/campaign-assets/');
          if (pathParts.length > 1) {
            const filePath = `campaign-assets/${pathParts[1]}`;
            // Attempt to delete the file from storage
            const { error: deleteError } = await supabase.storage
              .from('campaign-assets')
              .remove([filePath]);
              
            if (deleteError) {
              console.error('Error deleting QR code file:', deleteError);
              // Continue with database deletion even if storage deletion fails
            } else {
            }
          }
        } catch (pathError) {
          console.error('Error extracting file path for deletion:', pathError);
        }
      }

      const { error } = await supabase
        .from('qr_codes')
        .delete()
        .eq('id', qrCodeId)

      if (error) {
        console.error('Error deleting QR code:', error)
        return { success: false, error: error.message }
      }

      // Log QR code deletion
      Promise.resolve(UserLoggingService.logQRCodeDeleted(qrCodeId)).catch(err =>
        console.error('Failed to log QR code deletion:', err)
      );

      return { success: true }
    } catch (error) {
      console.error('Error deleting QR code:', error)
      return { success: false, error: 'Failed to delete QR code' }
    }
  },

  // Generate QR code URL (using QR server API)
  generateQRCodeUrl(text: string, size: number = 200): string {
    const baseUrl = 'https://api.qrserver.com/v1/create-qr-code/'
    const params = new URLSearchParams({
      size: `${size}x${size}`,
      data: text,
      format: 'png',
      ecc: 'M'
    })
    return `${baseUrl}?${params.toString()}`
  },

  // Calculate total scans for a QR code by counting rows in qr_scans table
  async getQRCodeScanCount(qrCodeId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('qr_scans')
        .select('*', { count: 'exact', head: true })
        .eq('qr_code_id', qrCodeId);

      if (error) {
        console.error('Error fetching scan count:', error);
        return 0;
      }

      return count || 0;
    } catch (error) {
      console.error('Error calculating scan count:', error);
      return 0;
    }
  },

  // Get QR code with calculated scan count
  async getQRCodeWithScans(qrCodeId: string): Promise<QRCodeServiceResponse> {
    try {
      const { data: qrCode, error } = await supabase
        .from('qr_codes')
        .select('*')
        .eq('id', qrCodeId)
        .single();

      if (error) {
        console.error('Error fetching QR code:', error);
        return { success: false, error: error.message };
      }

      // Calculate scan count dynamically
      const scanCount = await this.getQRCodeScanCount(qrCodeId);

      return {
        success: true,
        data: {
          ...qrCode,
          total_scans: scanCount
        }
      };
    } catch (error) {
      console.error('Error fetching QR code with scans:', error);
      return { success: false, error: 'Failed to fetch QR code with scans' };
    }
  },

  // Get multiple QR codes with scan counts (batch operation) with pagination
  async getQRCodesWithScans(
    qrCodeIds: string[],
    page: number = 1,
    pageSize: number = 50
  ): Promise<QRCodeServiceResponse & { total?: number; page?: number; pageSize?: number }> {
    try {
      if (!qrCodeIds || qrCodeIds.length === 0) {
        return { success: true, data: [], total: 0, page, pageSize };
      }

      const from = (page - 1) * pageSize;
      const to = from + pageSize - 1;

      // Fetch QR codes with pagination
      const { data: qrCodes, error: qrError, count } = await supabase
        .from('qr_codes')
        .select('*', { count: 'exact' })
        .in('id', qrCodeIds)
        .order('created_at', { ascending: false })
        .range(from, to);

      if (qrError) {
        console.error('Error fetching QR codes:', qrError);
        return { success: false, error: qrError.message };
      }

      // Fetch all scans for these QR codes (limit to reasonable number)
      const { data: scans, error: scansError } = await supabase
        .from('qr_scans')
        .select('qr_code_id')
        .in('qr_code_id', qrCodeIds)
        .limit(10000);

      if (scansError) {
        console.error('Error fetching scans:', scansError);
        return { success: false, error: scansError.message };
      }

      // Group scans by qr_code_id
      const scanCountMap = new Map<string, number>();
      (scans || []).forEach(scan => {
        const currentCount = scanCountMap.get(scan.qr_code_id) || 0;
        scanCountMap.set(scan.qr_code_id, currentCount + 1);
      });

      // Add scan counts to QR codes
      const qrCodesWithScans = (qrCodes || []).map(qrCode => ({
        ...qrCode,
        total_scans: scanCountMap.get(qrCode.id) || 0
      }));

      return { success: true, data: qrCodesWithScans, total: count || 0, page, pageSize };
    } catch (error) {
      console.error('Error fetching QR codes with scans:', error);
      return { success: false, error: 'Failed to fetch QR codes with scans' };
    }
  }
}
